import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Address from '@afosto/app-components-react/Address';
import Property from '@afosto/app-components-react/Property';
import getFullName from '@afosto/utils/getFullName';
import { fulfilment as fulfilmentApi } from '@afosto/fulfilment/api/Fulfilment';
import { methods as methodsApi } from '@afosto/fulfilment/api/Methods';
import {
  boolean as booleanFormatter,
  date as dateFormatter,
} from '@afosto/app-components-react/DataGrid/formatters';
import translations from './translations';

const ShippingDetailsSection = ({ shipping, shippingAddress }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['lcs:handling:read']);
  const [shippingMethod, setShippingMethod] = useState(null);
  const [servicePoint, setServicePoint] = useState(null);
  const { method_id: methodId, spid } = shipping ?? {};
  const { country_code: countryCode } = shippingAddress ?? {};

  useEffect(() => {
    let isMounted = true;

    const fetchShippingMethod = async () => {
      try {
        const response = await methodsApi.getShippingMethod(methodId);
        const { data = {} } = response;

        if (isMounted) {
          setShippingMethod(data);
        }

        return data;
      } catch (err) {
        if (isMounted) {
          setShippingMethod(null);
        }

        return null;
      }
    };

    const fetchServicePoint = async method => {
      try {
        const response = await fulfilmentApi.getServicePoint(
          spid,
          method?.carrier_code,
          countryCode,
        );
        const { data = null } = response;

        if (isMounted) {
          setServicePoint(data);
        }
      } catch (err) {
        if (isMounted) {
          setServicePoint(null);
        }
      }
    };

    const fetchShipping = async () => {
      try {
        const method = await fetchShippingMethod();

        if (method && spid && countryCode) {
          await fetchServicePoint(method);
        }
      } catch (err) {
        // Catch only
      }
    };

    if (methodId) {
      fetchShipping();
    }

    return () => {
      isMounted = false;
    };
  }, [methodId, spid, countryCode]);

  if (!hasPermissions()) {
    return (
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent>
          <UnauthorizedComponent showActions={false} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title={intl.formatMessage(translations.title)}
        titleTypographyProps={{ variant: 'h6' }}
      />
      <CardContent>
        <Property
          label={intl.formatMessage(translations.overview.shippingMethod)}
          value={shippingMethod?.name}
        />

        {shippingMethod?.is_carrier_location && (
          <Property
            label={intl.formatMessage(translations.overview.servicePoint)}
            value={
              servicePoint?.address && (
                <Address
                  company={servicePoint?.address?.organisation}
                  fullName={getFullName(servicePoint?.address)}
                  addressLine1={servicePoint?.address?.address_line_1}
                  addressLine2={servicePoint?.address?.address_line_2}
                  postalCode={servicePoint?.address?.postal_code}
                  city={servicePoint?.address?.locality}
                  country={servicePoint?.address?.country_code}
                  administrativeArea={servicePoint?.address?.administrative_area}
                />
              )
            }
          />
        )}

        <Property
          label={intl.formatMessage(translations.overview.isSplitAllowed)}
          value={booleanFormatter(shipping?.is_split_allowed)}
        />
        <Property
          label={intl.formatMessage(translations.overview.deliveryAt)}
          value={shipping?.window?.delivery_at && dateFormatter(shipping?.window?.delivery_at)}
        />
      </CardContent>
    </Card>
  );
};

ShippingDetailsSection.propTypes = {
  shipping: PropTypes.object,
  shippingAddress: PropTypes.object,
};

ShippingDetailsSection.defaultProps = {
  shipping: undefined,
  shippingAddress: undefined,
};

export default ShippingDetailsSection;
